import { useEffect, useState } from 'react';
import './ScrollFade.css';

/**
 * For a row that scrolls sideways (the module's tab strip on a phone, Insights' date chips): keeps the chosen item (`.is-active`)
 * in view, and says which edges have more behind them, so the row can fade there. Returns 'start', 'end', 'both' or ''; the
 * caller puts `scroll-fade scroll-fade--<that>` on the row (common/ScrollFade.css). `key` is whatever should centre the
 * chosen item again when it changes (the active tab, the number of tabs).
 */
export default function useScrollFade(ref, key) {
  const [fade, setFade] = useState('');
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const more = { start: el.scrollLeft > 4, end: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 };
      setFade(more.start && more.end ? 'both' : (more.start ? 'start' : (more.end ? 'end' : '')));
    };
    // The chosen item, centred where the row can scroll that far. Run again once the page font is in: the items are wider
    // then, and one placed against the old widths would end up cut at the edge.
    const centre = () => {
      const active = el.querySelector('.is-active');
      if (active && el.scrollWidth > el.clientWidth) {
        const left = active.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
        el.scrollLeft = Math.max(0, left - (el.clientWidth - active.offsetWidth) / 2);
      }
      update();
    };
    let cancelled = false;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!cancelled) centre(); });
    centre();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelled = true;
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref, key]);
  return fade;
}

/** The class names for a row, from what useScrollFade returned. */
export const scrollFadeClass = (fade) => (fade ? `scroll-fade scroll-fade--${fade}` : '');
